import type { AnnotationStroke, PlanDetail, SongDetail } from "../../../shared/types";

const DB_NAME = "mblive-stand";
const DB_VERSION = 2;

export interface CachedFile {
  id: string;
  mime: string;
  filename: string;
  bytes: ArrayBuffer;
  savedAt: string;
}

export interface CachedNotes {
  strokes: AnnotationStroke[];
  updatedAt: string;
  pending: boolean;
}

export interface StandMemory {
  itemId: string;
  page: number;
  extra: number;
  capo: number;
  view: "chart" | "pdf";
  font: number;
  mode: string;
  tempo: number | null;
  arrangementId?: string | null;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      for (const name of ["plans", "songs", "files", "notes", "positions"]) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open the stand cache"));
  });
}

function finish<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Stand cache request failed"));
  });
}

async function put<T>(store: string, value: T, key: string): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(store, "readwrite");
      transaction.objectStore(store).put(value, key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Stand cache write failed"));
      transaction.onabort = () => reject(transaction.error ?? new Error("Stand cache write was cancelled"));
    });
  } finally {
    db.close();
  }
}

async function read<T>(store: string, key: string): Promise<T | null> {
  const db = await openDb();
  try {
    const value = await finish(db.transaction(store).objectStore(store).get(key));
    return (value as T | undefined) ?? null;
  } finally {
    db.close();
  }
}

async function readAll<T>(store: string): Promise<T[]> {
  const db = await openDb();
  try {
    return await finish(db.transaction(store).objectStore(store).getAll()) as T[];
  } finally {
    db.close();
  }
}

export const listCachedPlans = () => readAll<PlanDetail>("plans");
export const listCachedSongs = () => readAll<SongDetail>("songs");

/** Remove private charts and notes when the current account signs out. */
export async function clearStandCache(): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const names = ["plans", "songs", "files", "notes", "positions"];
      const transaction = db.transaction(names, "readwrite");
      for (const name of names) transaction.objectStore(name).clear();
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Could not clear the stand cache"));
      transaction.onabort = () => reject(transaction.error ?? new Error("Stand cache clearing was cancelled"));
    });
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith("mb-stand-memory:")) sessionStorage.removeItem(key);
    }
  } finally {
    db.close();
  }
}

export const putPlan = (plan: PlanDetail) => put("plans", plan, plan.id);
export const getPlan = (planId: string) => read<PlanDetail>("plans", planId);
export const putSong = (song: SongDetail) => put("songs", song, song.id);
export const getSong = (songId: string) => read<SongDetail>("songs", songId);
export const putFile = (file: CachedFile) => put("files", file, file.id);
export const getFile = (fileId: string) => read<CachedFile>("files", fileId);

export function noteKey(songId: string, arrangementId: string | null, planId: string | null, page: number): string {
  return `${songId}|${arrangementId ?? ""}|${planId ?? ""}|${page}`;
}

export const putNotes = (key: string, notes: CachedNotes) => put("notes", notes, key);
export const getNotes = (key: string) => read<CachedNotes>("notes", key);
export async function listPendingNotes(): Promise<{ key: string; notes: CachedNotes }[]> {
  const db = await openDb();
  try {
    const transaction = db.transaction("notes");
    const store = transaction.objectStore("notes");
    const [keys, notes] = await Promise.all([
      finish(store.getAllKeys()),
      finish(store.getAll()) as Promise<CachedNotes[]>,
    ]);
    return notes.flatMap((entry, index) => entry.pending && typeof keys[index] === "string"
      ? [{ key: keys[index] as string, notes: entry }]
      : []);
  } finally {
    db.close();
  }
}
export const putPosition = (planId: string, memory: StandMemory) => put("positions", memory, planId);
export const getPosition = (planId: string) => read<StandMemory>("positions", planId);

const sessionKey = (planId: string) => `mb-stand-memory:${planId}`;

export function readSessionMemory(planId: string): StandMemory | null {
  try {
    const raw = sessionStorage.getItem(sessionKey(planId));
    return raw ? (JSON.parse(raw) as StandMemory) : null;
  } catch {
    return null;
  }
}

export function writeSessionMemory(planId: string, memory: StandMemory): void {
  try {
    sessionStorage.setItem(sessionKey(planId), JSON.stringify(memory));
  } catch {
    /* private mode can refuse storage; the in-memory stand still works */
  }
}
